import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WIN_IMG = `${process.env.PUBLIC_URL || ""}/showdown/win.png`;
const LOSS_IMG = `${process.env.PUBLIC_URL || ""}/showdown/loss.png`;

// ---------------------------------------------------------------------------
// One-time WIN / LOSS / TIE result popup. Score + opponent come straight from the
// persisted matchup (backend is the source of truth). Appears once, then acked.
// ---------------------------------------------------------------------------
function ResultModal({ popup, onClose }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const isWin = popup.result === "WIN";
  const isLoss = popup.result === "LOSS";
  const isTie = popup.result === "TIE";

  const ack = useCallback(async (go) => {
    setBusy(true);
    try { await api.post(`/showdown/result-popups/${popup.matchup_id}/ack`); } catch { /* best-effort */ }
    onClose();
    if (go) navigate("/showdown");
  }, [popup.matchup_id, onClose, navigate]);

  const title = isWin ? "YOU WON" : isLoss ? "YOU LOST" : "IT'S A TIE";
  const accent = isWin ? "text-emerald-400" : isLoss ? "text-red-400" : "text-amber-400";
  const ring = isWin ? "border-emerald-500/40" : isLoss ? "border-red-500/40" : "border-amber-500/40";

  return (
    <div data-testid="showdown-result-modal"
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className={`relative w-full max-w-md rounded-2xl border ${ring} bg-[#070707] overflow-hidden shadow-2xl pp-fade-up max-h-[92vh] overflow-y-auto`}>
        <button data-testid="result-close" onClick={() => ack(false)} disabled={busy}
          className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-black/60 border border-white/15 flex items-center justify-center text-zinc-300 hover:text-white transition-colors">
          <X className="h-4 w-4" />
        </button>

        {!isTie && (
          <img src={isWin ? WIN_IMG : LOSS_IMG} alt={title}
            className="w-full object-cover select-none pointer-events-none"
            onError={(e) => { e.currentTarget.style.display = "none"; }} />
        )}

        <div className={`px-6 ${isTie ? "pt-10" : "pt-5"} pb-6 text-center`}>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">{popup.week_label}</div>
          <div data-testid="result-outcome" className={`font-display text-3xl font-black mt-1 ${accent}`}>{title}</div>
          {popup.decided_by === "tiebreaker" && !isTie && (
            <div className="text-[11px] text-zinc-400 mt-1">Decided by the combined-score tiebreaker</div>
          )}

          <div className="mt-5 space-y-2">
            <div data-testid="result-me-row"
              className={`flex items-center justify-between rounded-xl px-4 py-3 border ${isWin ? "border-emerald-500/40 bg-emerald-500/10" : isLoss ? "border-red-500/30 bg-red-500/5" : "border-amber-500/30 bg-amber-500/5"}`}>
              <span className="font-display font-bold text-white truncate">{popup.me.display_name}</span>
              <span className={`font-display text-2xl font-black ${accent}`}>{popup.me.total}</span>
            </div>
            <div data-testid="result-opp-row"
              className="flex items-center justify-between rounded-xl px-4 py-3 border border-[#1F1F22] bg-[#0A0A0A]">
              <span className="font-display font-bold text-zinc-300 truncate">{popup.opponent.display_name}</span>
              <span className="font-display text-2xl font-black text-zinc-400">{popup.opponent.total}</span>
            </div>
          </div>

          <button data-testid="result-back-to-league" onClick={() => ack(true)} disabled={busy}
            className={`mt-6 w-full py-3 rounded-full font-bold active:scale-95 transition-transform flex items-center justify-center gap-2 ${
              isWin ? "bg-emerald-600 text-white" : isLoss ? "bg-[#D50A0A] text-white" : "bg-amber-500 text-black"}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>BACK TO LEAGUE <ArrowRight className="h-4 w-4" /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One-time Winner-only rule-change notice. Non-dismissible until the player scrolls
// to the bottom; then the close/continue control enables. Shown once per version.
// ---------------------------------------------------------------------------
function NoticeModal({ cutoverWeek, onClose }) {
  const scrollRef = useRef(null);
  const [reached, setReached] = useState(false);
  const [busy, setBusy] = useState(false);
  const wk = cutoverWeek || "—";

  const checkBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 12) setReached(true);
  }, []);

  useEffect(() => {
    // If content fits without scrolling, enable immediately (keeps accessibility sane).
    const el = scrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 12) setReached(true);
  }, []);

  const ack = useCallback(async () => {
    setBusy(true);
    try { await api.post("/showdown/notice/ack"); } catch { /* best-effort */ }
    onClose();
  }, [onClose]);

  const Section = ({ title, children }) => (
    <div className="mt-4">
      <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#ff7a7a]">{title}</div>
      <div className="text-sm text-zinc-300 mt-1 space-y-1 leading-relaxed">{children}</div>
    </div>
  );

  return (
    <div data-testid="showdown-notice-modal"
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-lg rounded-2xl border border-[#D50A0A]/40 bg-[#070707] overflow-hidden shadow-2xl pp-fade-up flex flex-col max-h-[90vh]">
        {reached && (
          <button data-testid="showdown-notice-close" onClick={ack} disabled={busy}
            className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-black/60 border border-white/15 flex items-center justify-center text-zinc-300 hover:text-white transition-colors">
            <X className="h-4 w-4" />
          </button>
        )}
        <div className="px-6 pt-6 pb-3 border-b border-[#1F1F22]">
          <h2 className="font-display text-xl font-black tracking-tight">🏈 PICKHOUSE SHOWDOWN UPDATE</h2>
          <p className="text-sm text-zinc-400 mt-1">A few things are changing. Your old scores aren't.</p>
        </div>

        <div ref={scrollRef} onScroll={checkBottom} data-testid="showdown-notice-scroll"
          className="px-6 py-4 overflow-y-auto overflow-x-hidden grow">
          <p className="text-sm text-zinc-300 leading-relaxed">
            Quick heads-up before you get back to your picks. We're simplifying PickHouse Showdown going forward.
          </p>

          <Section title="What's Changing">
            <p>Starting with <b className="text-white">Regular Season Week {wk}</b>, Showdown will be based on your NFL game Winner picks.</p>
            <p>Passing Yards and Rushing Yards picks are being removed from new weeks.</p>
            <p><span className="text-emerald-400 font-semibold">Your previous scores, points, wins, losses and matchups are NOT being changed.</span> Everything you've already earned stays exactly as it is.</p>
          </Section>

          <Section title="How Scoring Works">
            <p>Correct Winner picks are worth more as the season goes on:</p>
            <ul className="list-disc pl-5 space-y-0.5">
              <li>Weeks 1–6: <b className="text-white">1 point</b> each</li>
              <li>Weeks 7–12: <b className="text-white">2 points</b> each</li>
              <li>Weeks 13–18: <b className="text-white">3 points</b> each</li>
            </ul>
            <p>Your weekly total goes head-to-head against your scheduled opponent. Higher score wins the matchup.</p>
          </Section>

          <Section title="Tiebreaker">
            <p>The combined-score prediction is still important, but it is ONLY used if you and your opponent finish the week with the exact same Showdown score. Closest prediction wins. If the tiebreaker is also tied, the matchup ends in a Tie.</p>
          </Section>

          <Section title="Standings">
            <p>Your head-to-head record remains the most important thing. Standings are ranked by:</p>
            <ol className="list-decimal pl-5 space-y-0.5">
              <li>Record</li>
              <li>Total Showdown points</li>
              <li>Tiebreaker, only when needed</li>
            </ol>
          </Section>

          <Section title="Match Results">
            <p>When a matchup finishes, you'll get a one-time result popup letting you know if you won or lost. And yes... the loser gets the embarrassing one.</p>
          </Section>

          <p className="text-sm text-zinc-300 mt-4">That's it. Make your picks and good luck.</p>
          <div className="h-2" />
        </div>

        <div className="px-6 py-4 border-t border-[#1F1F22]">
          <button data-testid="showdown-notice-continue" onClick={ack} disabled={!reached || busy}
            className="w-full py-3 rounded-full bg-[#D50A0A] text-white font-bold active:scale-95 transition-transform disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "✓ I'VE READ IT — LET'S GO"}
          </button>
          {!reached && (
            <p className="text-[11px] text-zinc-500 text-center mt-2">Scroll to the bottom to continue.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export function ShowdownModals() {
  const { user } = useAuth();
  const [notice, setNotice] = useState(null);     // {show, cutover_week}
  const [popups, setPopups] = useState([]);        // queue of unseen results
  const [idx, setIdx] = useState(0);
  const isParticipant = user && (user.role === "player" || user.role === "owner" || user.role === "admin");

  const loadResults = useCallback(async () => {
    try {
      const { data } = await api.get("/showdown/result-popups");
      setPopups(data.popups || []);
      setIdx(0);
    } catch { setPopups([]); }
  }, []);

  useEffect(() => {
    if (!user) { setNotice(null); setPopups([]); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/showdown/notice");
        if (alive) setNotice(data);
      } catch { if (alive) setNotice({ show: false }); }
      await loadResults();
    })();
    return () => { alive = false; };
  }, [user, loadResults]);

  if (!user || !isParticipant) return null;

  // Rule-change notice takes priority; result popups show after it's acknowledged.
  if (notice && notice.show) {
    return <NoticeModal cutoverWeek={notice.cutover_week} onClose={() => setNotice({ show: false })} />;
  }

  const current = popups[idx];
  if (current) {
    return <ResultModal popup={current} onClose={() => setIdx((i) => i + 1)} />;
  }
  return null;
}
